import { useCallback } from 'react';
import { FormikHelpers } from 'formik';
import { MutationTuple, OperationVariables } from '@apollo/client';
import { useNotification } from '@/common/components/notification/NotificationContext';

interface UseFormMutationOptions<
  TValues,
  TData,
  TVariables extends OperationVariables,
> {
  mutation:
    | MutationTuple<TData, TVariables>
    | [MutationTuple<TData, TVariables>, { loading?: boolean }];
  mapValuesToVariables: (values: TValues) => TVariables;
  onSuccess?: (data: TData, values: TValues) => void | Promise<void>;
  onError?: (error: Error, values: TValues) => void;
  successMessage?: string;
  errorMessage?: string;
  refetch?: () => void | Promise<void>;
}

export function useFormMutation<
  TValues,
  TData,
  TVariables extends OperationVariables,
>({
  mutation,
  mapValuesToVariables,
  onSuccess,
  onError,
  successMessage,
  errorMessage,
  refetch,
}: UseFormMutationOptions<TValues, TData, TVariables>) {
  // Handle both MutationTuple and array format [MutationTuple, { loading }]
  const isArrayFormat = Array.isArray(mutation) && Array.isArray(mutation[0]);
  const mutationTuple: MutationTuple<TData, TVariables> = isArrayFormat
    ? (mutation[0] as MutationTuple<TData, TVariables>)
    : (mutation as MutationTuple<TData, TVariables>);
  const [mutate, mutationState] = mutationTuple;
  const loading =
    isArrayFormat && mutation[1]?.loading !== undefined
      ? mutation[1].loading
      : mutationState.loading;
  const { showSuccess, showError } = useNotification();

  const handleSubmit = useCallback(
    async (values: TValues, formikHelpers: FormikHelpers<TValues>) => {
      try {
        const variables = mapValuesToVariables(values);
        const result = await mutate({ variables });

        if (result.errors && result.errors.length > 0) {
          const error = new Error(result.errors[0].message);
          if (onError) {
            onError(error, values);
          } else {
            showError(
              errorMessage ||
                result.errors[0].message ||
                'Failed to submit form'
            );
          }
          formikHelpers.setSubmitting(false);
          return;
        }

        if (result.data) {
          if (successMessage) {
            showSuccess(successMessage);
          }
          if (refetch) {
            await refetch();
          }
          if (onSuccess) {
            await onSuccess(result.data, values);
          }
          formikHelpers.setSubmitting(false);
        }
      } catch (error) {
        console.error('Form submission error:', error);
        const errorObj =
          error instanceof Error ? error : new Error('Unknown error');
        if (onError) {
          onError(errorObj, values);
        } else {
          showError(
            errorMessage || errorObj.message || 'Failed to submit form'
          );
        }
        formikHelpers.setSubmitting(false);
      }
    },
    [
      mutate,
      mapValuesToVariables,
      onSuccess,
      onError,
      successMessage,
      errorMessage,
      refetch,
      showSuccess,
      showError,
    ]
  );

  return {
    handleSubmit,
    loading,
  };
}
